// Screenshot of the app for a β report: the #app tree drawn to a JPEG (modern-screenshot, loaded on first use).
// The β button and sheet live outside #app, so they are never in the picture. Videos are skipped: the scene's still
// image sits under each loop video, so the picture still shows the place. Gives up after 8 s (the report still goes).
import type { FeedbackImage } from '@shared/types';

const MAX_SIDE = 1600;
const TIMEOUT_MS = 8000;

export async function captureApp(): Promise<{ shot: FeedbackImage | null; error?: string; ms: number }> {
  const t0 = performance.now();
  const root = document.getElementById('app');
  if (!root) return { shot: null, error: 'no #app', ms: 0 };
  try {
    const { domToJpeg } = await import('modern-screenshot');
    const w = Math.max(1, root.clientWidth), h = Math.max(1, root.clientHeight);
    const scale = Math.max(0.5, Math.min(window.devicePixelRatio || 1, 2, MAX_SIDE / Math.max(w, h)));
    const url = await Promise.race([
      domToJpeg(root, {
        quality: 0.72, scale, backgroundColor: '#0A0908', timeout: 5000,
        filter: (n) => !(n instanceof HTMLVideoElement),
      }),
      new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timed out')), TIMEOUT_MS)),
    ]);
    const b64 = url.slice(url.indexOf(',') + 1);
    if (!/^data:image\/jpeg/.test(url) || b64.length < 100) throw new Error('empty image');
    return { shot: { b64, mime: 'image/jpeg' }, ms: Math.round(performance.now() - t0) };
  } catch (e) {
    return { shot: null, error: String((e as Error)?.message || e).slice(0, 200), ms: Math.round(performance.now() - t0) };
  }
}

/** A picture the tester picked (e.g. a notification screenshot): JPEG, longest side <= 1600 px. */
export async function readPicture(file: File): Promise<FeedbackImage> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error('bad image')); i.src = url; });
    const k = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const cv = document.createElement('canvas');
    cv.width = Math.max(1, Math.round(img.naturalWidth * k)); cv.height = Math.max(1, Math.round(img.naturalHeight * k));
    const g = cv.getContext('2d')!;
    g.fillStyle = '#000'; g.fillRect(0, 0, cv.width, cv.height);
    g.drawImage(img, 0, 0, cv.width, cv.height);
    const data = cv.toDataURL('image/jpeg', 0.8);
    return { b64: data.slice(data.indexOf(',') + 1), mime: 'image/jpeg' };
  } finally { URL.revokeObjectURL(url); }
}

export const dataUrl = (img: FeedbackImage) => `data:${img.mime};base64,${img.b64}`;
